import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { Stack } from '~shared/components/Stack';
import {
  AnswerData,
  calculateAggregateAnswerData,
} from '~teacher/components/ResultsQuestion/util/calculateAggregateAnswerData';
import { StudentActivityResponse } from '~teacher/models/activities';
import { QuizAnswer } from '~teacher/models/quizzes';

import { calculatePercent } from '../../../util/math';
import { ResultsQuestionAnswerResult } from './ResultsQuestionAnswerResult';
import { CorrectAnswerIndicator } from './ResultsQuestionAnswers';

interface Props {
  answers: QuizAnswer[];
  responses: StudentActivityResponse[];
  showResults: boolean;
  hasCorrectAnswer: boolean;
}

export function ResultsQuestionAnswersTF({
  answers,
  showResults,
  responses,
  hasCorrectAnswer,
}: Props) {
  const indicatorClass = cx(
    'relative xl:absolute flex w-12 h-9 transition-all ease-in-out duration-200',
    {
      'max-w-0 opacity-0': !showResults,
      'max-w-12 xl:-ml-12 opacity-100': showResults,
    }
  );

  const aggregateAnswerData = calculateAggregateAnswerData(answers, responses);

  return (
    <Stack space="4">
      {answers.map((answer) => {
        const eachIndicatorClass = cx(
          indicatorClass,
          !answer.isCorrect && showResults ? 'delay-300' : ''
        );

        const answerData: AnswerData =
          aggregateAnswerData.answerData[answer.id];
        const score = answerData
          ? calculatePercent({
              numerator: answerData.numberResponses,
              denominator: aggregateAnswerData.totalSingleResponses,
            })
          : 0;

        return (
          <div
            className="flex items-center pr-12"
            key={answer.id}
            data-testid={`tf-answer-${answer.id}`}
          >
            <div className={eachIndicatorClass}>
              <div
                className="absolute top-0 right-0 w-9 h-9 mr-3 transition-transform ease-in-out duration-200"
                style={{ transform: `translateX(${showResults ? 0 : '3rem'})` }}
              >
                {answer.isCorrect ? (
                  <CorrectAnswerIndicator />
                ) : (
                  <div className="w-9" />
                )}
              </div>
            </div>
            <ResultsQuestionAnswerResult
              score={score}
              showResult={showResults}
              isCorrect={answer.isCorrect}
              hasCorrectAnswer={hasCorrectAnswer}
            >
              {translate(answer.text)}
            </ResultsQuestionAnswerResult>
          </div>
        );
      })}
    </Stack>
  );
}
